import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { Button, Grid } from 'semantic-ui-react';
import { UpsertReasonForEncounterDto } from 'ts-types/api.types';
import { addReasonForEncounter, editReasonForEncounter } from 'services/reasonForEncounterServices';
import Input from 'components/final-form/Input';
import CheckBox from 'components/final-form/CheckBox';
import { composeValidators, maxChars, required } from 'utils/validatorUtils';
import axios, { CancelTokenSource } from 'axios';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';

interface Props extends MessageSourceProps {
  toggleAddEditModal: () => void,
  onCancel: () => void,
  onSave: () => void,
  reasonForEncounterId?: number,
  reasonForEncounter?: UpsertReasonForEncounterDto
}

interface State {
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

class UpsertReasonsForEncounterForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      cancelTokenSource
    }

  }

  handleSubmit = async (values: Partial<UpsertReasonForEncounterDto>) => {

    let saveValues: Partial<UpsertReasonForEncounterDto> = {...values};

    const {reasonForEncounterId, getMessage, onSave} = this.props;
    const {cancelTokenSource} = this.state;

    const handleError = () => {
      this.setState({
        errorMessage: getMessage("error.general")
      })
    };

    if (reasonForEncounterId) {
      await editReasonForEncounter(reasonForEncounterId, saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    } else {
      await addReasonForEncounter(saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    const defaultValues: Partial<UpsertReasonForEncounterDto> = {
      basisReason: false
    };

    const {reasonForEncounter = defaultValues} = this.props;

    return (
        <FinalForm
            onSubmit={(values => this.handleSubmit(values))}
            initialValues={reasonForEncounter}
            subscription={{pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
                {errorMessage
                && (
                    <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                      {errorMessage}
                    </StyledErrorMessage>
                )
                }
                <PraxFormGrid>
                  <Grid.Row>
                    <Grid.Column width={5}>
                      {getMessage("reasonForEncounter.code")}
                    </Grid.Column>
                    <Grid.Column width={5}>
                      <Field
                          fluid
                          name="code"
                          component={Input}
                          validate={composeValidators(maxChars(10), required)}
                          autoFocus
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5}>
                      {getMessage("reasonForEncounter.description")}
                    </Grid.Column>
                    <Grid.Column width={11}>
                      <Field
                          fluid
                          name="description"
                          component={Input}
                          validate={required}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5}>
                      {getMessage("reasonForEncounter.descriptionfr")}
                    </Grid.Column>
                    <Grid.Column width={11}>
                      <Field
                          fluid
                          name="descriptionFR"
                          component={Input}
                          validate={required}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5}>
                      {getMessage("reasonForEncounter.descriptionit")}
                    </Grid.Column>
                    <Grid.Column width={11}>
                      <Field
                          fluid
                          name="descriptionIT"
                          component={Input}
                          validate={required}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5}>
                      {getMessage("reasonForEncounter.descriptionen")}
                    </Grid.Column>
                    <Grid.Column width={11}>
                      <Field
                          fluid
                          name="descriptionEN"
                          component={Input}
                          validate={required}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5}>
                    </Grid.Column>
                    <Grid.Column width={5}>
                      <Field
                          name="basisReason"
                          component={CheckBox}
                          label={`${getMessage("reasonForEncounter.basisreason")}`}
                      />
                    </Grid.Column>
                  </Grid.Row>
                </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="Submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

}

export default withMessages(UpsertReasonsForEncounterForm)
